import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { IBusinessCategory } from '../../../_interfaces/IBusinessCategory';
import {
  ICustomerAccountAggregate,
  ICustomerBillingPreview,
  ICustomerFiscalProfile,
  ICustomerModuleAccess,
  ICustomerPricingTerm,
  ICustomerStorageSettings,
} from '../../../_interfaces/ISuperadminCustomerAccount';
import { BusinessCategoriesService } from '../../../_services/business-categories/business-categories.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import { FormOutboundEmailAccountComponent } from '../../../components/forms/form-outbound-email-account/form-outbound-email-account.component';

@Component({
  selector: 'app-admin-customer-account',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    FormOutboundEmailAccountComponent,
  ],
  templateUrl: './admin-customer-account.component.html',
  styleUrl: './admin-customer-account.component.scss',
})
export class AdminCustomerAccountComponent implements OnInit {
  loading = true;
  saving = false;
  customerId = 0;
  month = new Date().toISOString().slice(0, 7);
  activeTab:
    | 'fiscal'
    | 'categories'
    | 'modules'
    | 'pricing'
    | 'storage'
    | 'preview'
    | 'email' =
    'fiscal';
  activeFiscalSubTab: 'general' | 'fiscal' | 'contract' = 'general';

  account: ICustomerAccountAggregate | null = null;
  preview: ICustomerBillingPreview | null = null;

  fiscalDraft: ICustomerFiscalProfile = {};
  contractDraft: any = {};
  categoriesDraft: number[] = [];
  modulesDraft: ICustomerModuleAccess[] = [];
  pricingDraft: ICustomerPricingTerm[] = [];
  storageDraft: ICustomerStorageSettings = {
    quota_mb: 100,
    used_mb: 0,
    free_mb: 100,
    usage_percent: 0,
    block_on_limit: true,
  };
  categoryOptions: Array<IBusinessCategory & { displayName: string }> = [];

  readonly modeOptions = [
    {
      labelKey: 'ADMIN.CUSTOMERS.PRICING.MODE.GLOBAL_LINKED',
      value: 'GLOBAL_LINKED',
    },
    {
      labelKey: 'ADMIN.CUSTOMERS.PRICING.MODE.CUSTOM_LOCKED',
      value: 'CUSTOM_LOCKED',
    },
  ];

  readonly contractStatusOptions = [
    { labelKey: 'ADMIN.CUSTOMERS.CONTRACT.STATUS.DRAFT', value: 'DRAFT' },
    { labelKey: 'ADMIN.CUSTOMERS.CONTRACT.STATUS.ACTIVE', value: 'ACTIVE' },
    {
      labelKey: 'ADMIN.CUSTOMERS.CONTRACT.STATUS.SUSPENDED',
      value: 'SUSPENDED',
    },
    {
      labelKey: 'ADMIN.CUSTOMERS.CONTRACT.STATUS.CANCELLED',
      value: 'CANCELLED',
    },
  ];

  readonly customerStatusOptions = [
    { labelKey: 'COMMON.ACTIVE', value: 'active' },
    { labelKey: 'COMMON.INACTIVE', value: 'inactive' },
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly customersService: CustomersService,
    private readonly businessCategoriesService: BusinessCategoriesService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.customerId = Number(this.route.snapshot.paramMap.get('id') || 0);
    this.loadCategoryOptions();
    this.load();
  }

  private getTranslatedCategoryName(
    category: Pick<IBusinessCategory, 'code' | 'name'>,
  ): string {
    const code = `${category?.code || ''}`.trim();
    const fallback = `${category?.name || ''}`.trim();
    if (!code) {
      return fallback;
    }

    const key = `BUSINESS_CATEGORIES.${code}`;
    const translated = `${this.translate.instant(key) || ''}`.trim();
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  }

  private loadCategoryOptions() {
    this.businessCategoriesService.getAll().subscribe({
      next: (rows) => {
        this.categoryOptions = (rows || [])
          .filter((row) => !!row.active)
          .map((row) => ({
            ...row,
            displayName: this.getTranslatedCategoryName(row),
          }));
      },
      error: () => {
        this.categoryOptions = [];
      },
    });
  }

  load() {
    if (!this.customerId) return;
    this.loading = true;
    this.customersService.getCustomerAccount(this.customerId, this.month).subscribe({
      next: (account) => {
        this.account = account;
        this.preview = account.preview || null;
        this.loadDrafts();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private loadDrafts() {
    if (!this.account) return;
    this.fiscalDraft = { ...(this.account.fiscal || {}) };
    this.contractDraft = {
      ...(this.account.contract || {}),
      starts_at: this.toDateInput(this.account.contract?.starts_at),
      cycle_anchor_at: this.toDateInput(this.account.contract?.cycle_anchor_at),
      next_billing_at: this.toDateInput(this.account.contract?.next_billing_at),
      next_cycle_start_at: this.toDateInput(this.account.contract?.next_cycle_start_at),
      billing_timezone: this.account.contract?.billing_timezone || 'Europe/Madrid',
      cycle_days: Number(this.account.contract?.cycle_days || 28),
      ends_at: this.toDateInput(this.account.contract?.ends_at),
    };
    this.categoriesDraft = (this.account.categories || [])
      .map((row) => Number(row.id || 0))
      .filter((id) => id > 0);
    this.modulesDraft = (this.account.modules || []).map((item) => {
      const normalized = { ...item };
      if (this.isCoreModule(normalized)) {
        normalized.enabled = true;
        normalized.disabled_reason = null;
      }
      return normalized;
    });
    this.pricingDraft = (this.account.pricing || []).map((item) => ({ ...item }));
    this.storageDraft = {
      quota_mb: Number(this.account.storage?.quota_mb || 100),
      used_mb: Number(this.account.storage?.used_mb || 0),
      free_mb: Number(this.account.storage?.free_mb || 0),
      usage_percent: Number(this.account.storage?.usage_percent || 0),
      block_on_limit: !!this.account.storage?.block_on_limit,
    };
  }

  saveFiscal() {
    if (this.saving) return;
    this.saving = true;
    this.customersService.updateCustomerFiscal(this.customerId, this.fiscalDraft).subscribe({
      next: () => {
        this.saving = false;
        this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
        this.load();
      },
      error: () => {
        this.saving = false;
        this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
      },
    });
  }

  saveContract() {
    if (this.saving) return;
    this.saving = true;
    this.customersService
      .updateCustomerContract(this.customerId, {
        status: this.contractDraft.status,
        billing_cycle: 'DAYS_28',
        cycle_days: Number(this.contractDraft.cycle_days || 28),
        starts_at: this.fromDateInput(this.contractDraft.starts_at),
        cycle_anchor_at: this.fromDateInput(this.contractDraft.cycle_anchor_at),
        billing_timezone: this.contractDraft.billing_timezone || null,
        next_billing_at: this.fromDateInput(this.contractDraft.next_billing_at),
        next_cycle_start_at: this.fromDateInput(this.contractDraft.next_cycle_start_at),
        ends_at: this.fromDateInput(this.contractDraft.ends_at),
        account_manager: this.contractDraft.account_manager || null,
        contract_notes: this.contractDraft.contract_notes || null,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
          this.load();
        },
        error: () => {
          this.saving = false;
          this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
        },
      });
  }

  saveCategories() {
    if (this.saving) return;
    this.saving = true;
    const business_category_ids = [...new Set((this.categoriesDraft || []).map((id) => Number(id)))]
      .filter((id) => Number.isInteger(id) && id > 0);

    this.customersService
      .updateCustomerCategories(this.customerId, { business_category_ids })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
          this.load();
        },
        error: () => {
          this.saving = false;
          this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
        },
      });
  }

  saveModules() {
    if (this.saving) return;
    this.saving = true;
    this.customersService
      .updateCustomerModules(this.customerId, {
        modules: (this.modulesDraft || []).map((item) => ({
          modules_id: item.modules_id,
          enabled: !!item.enabled,
        })),
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
          this.load();
        },
        error: (error) => {
          this.saving = false;
          const code = `${error?.error?.message || ''}`;
          if (code.includes('MODULE_REQUIRED')) {
            this.toast('warn', 'ADMIN.CUSTOMERS.MODULES.MODULE_REQUIRED');
            return;
          }
          this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
        },
      });
  }

  readonly loadEmailAccount = () => this.customersService.getCustomerEmailAccount(this.customerId);
  readonly saveEmailAccount = (payload: any) =>
    this.customersService.updateCustomerEmailAccount(this.customerId, payload);
  readonly testEmailAccount = (to?: string) =>
    this.customersService.testCustomerEmailAccount(this.customerId, to);

  savePricing() {
    if (this.saving) return;
    this.saving = true;
    this.customersService
      .updateCustomerPricing(this.customerId, {
        terms: (this.pricingDraft || []).map((item) => ({
          concept_type: item.concept_type,
          modules_id: item.modules_id || undefined,
          mode: item.mode,
          discount_percent: Number(item.discount_percent || 0),
          locked_base_price:
            item.mode === 'CUSTOM_LOCKED'
              ? Number(item.locked_base_price || 0)
              : null,
          active: !!item.active,
        })),
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
          this.load();
        },
        error: () => {
          this.saving = false;
          this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
        },
      });
  }

  saveStorage() {
    if (this.saving) return;
    this.saving = true;
    this.customersService
      .updateCustomerStorage(this.customerId, {
        quota_mb: Number(this.storageDraft.quota_mb || 0),
        block_on_limit: !!this.storageDraft.block_on_limit,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.SAVED');
          this.load();
        },
        error: () => {
          this.saving = false;
          this.toast('error', 'ADMIN.CUSTOMERS.ACCOUNT.MSG.ERROR');
        },
      });
  }

  refreshPreview() {
    if (!this.customerId) return;
    this.customersService.getCustomerBillingPreview(this.customerId, this.month).subscribe({
      next: (preview) => {
        this.preview = preview;
      },
      error: () => {},
    });
  }

  isCoreModule(row: ICustomerModuleAccess | null | undefined): boolean {
    const identifier = `${row?.module?.identifier || ''}`.trim().toLowerCase();
    return identifier === 'appointments' || identifier === 'patients';
  }

  getModuleLabel(row: ICustomerModuleAccess | null | undefined): string {
    const identifier = `${row?.module?.identifier || ''}`.trim();
    if (identifier) {
      const key = `ADMIN.CUSTOMERS.MODULE_LABELS.${identifier.toUpperCase()}`;
      const translated = `${this.translate.instant(key) || ''}`.trim();
      if (translated && translated !== key) {
        return translated;
      }
    }

    const rawName = `${row?.module?.name || ''}`.trim();
    if (rawName && rawName.includes('.')) {
      const translated = `${this.translate.instant(rawName) || ''}`.trim();
      if (translated && translated !== rawName) {
        return translated;
      }
    }

    return rawName || identifier || this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.MODULE');
  }

  getConceptLabel(row: ICustomerPricingTerm | any): string {
    if (row.concept_type === 'STAFF_USER') {
      return this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.STAFF_USER');
    }
    if (row.concept_type === 'SITE') {
      return this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.SITE');
    }
    if (row.concept_type === 'STORAGE_MB') {
      return this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.STORAGE_MB');
    }
    return this.getModuleLabel(row);
  }

  getStorageUsageSeverity(): 'ok' | 'warn' | 'danger' {
    if (this.getStorageFreeMb() <= 0) {
      return 'danger';
    }
    if (this.getStorageUsagePercent() >= 85) {
      return 'warn';
    }
    return 'ok';
  }

  getStorageFreeMb(): number {
    const quota = Number(this.storageDraft.quota_mb || 0);
    const used = Number(this.storageDraft.used_mb || 0);
    return Math.max(0, quota - used);
  }

  getStorageUsagePercent(): number {
    const quota = Number(this.storageDraft.quota_mb || 0);
    const used = Number(this.storageDraft.used_mb || 0);
    if (quota <= 0) {
      return 0;
    }
    return Math.min(100, (used / quota) * 100);
  }

  private toDateInput(value?: string | Date | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  private fromDateInput(value?: string): string | null {
    const raw = `${value || ''}`.trim();
    if (!raw) return null;
    return `${raw}T00:00:00.000Z`;
  }

  private toast(severity: 'success' | 'warn' | 'error', detailKey: string) {
    this.messageService.add({
      severity,
      summary: this.translate.instant(
        severity === 'success'
          ? 'COMMON.SUCCESS'
          : severity === 'warn'
            ? 'COMMON.WARNING'
            : 'COMMON.ERROR',
      ),
      detail: this.translate.instant(detailKey),
    });
  }
}
